import styled from '@emotion/styled'
import type { FC, PropsWithChildren } from 'react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import CardBase, * as Card from '@segment/matcha/recipes/Card'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Recommended reading`,
}

const data = [
  {
    title: `CDP Buyer’s Guide`,
    description: `Our buyer’s guide for CDPs will help you understand when it’s time to buy a CDP, which features you need, and how to evaluate the various CDP vendors out there.`,
    href: `https://learn.segment.com/cdp-buying-guide/?utm_source=content&utm_medium=referral&utm_campaign=all_cnt_growth-report_`,
  },
  {
    title: `Ultimate Guide to Customer Retention`,
    description: `Learn how to avoid rising customer acquisition costs and increase customer engagement with proven retention strategies.`,
    href: `https://learn.segment.com/the-ultimate-guide-to-customer-retention/?utm_source=content&utm_medium=referral&utm_campaign=all_cnt_growth-report_20220929&utm_content=ebook`,
  },
  {
    title: `State of Personalization Report`,
    description: `We surveyed businesses and consumers around the world to explore how attitudes, preferences, and experiences with personalization have evolved over the past year.`,
    href: `/state-of-personalization-report/?utm_source=content&utm_medium=referral&utm_campaign=all_cnt_growth-report_`,
  },
]

const RecommendedReading: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  return (
    <Background>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Heading variant="h2" tag="h2">
              {copy.heading}
            </CopyText.Heading>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <ColumnTrack
            columns={1}
            overrides={{
              md: 2,
              xl: 3,
            }}
          >
            {data.map((item) => (
              <CardBase key={item.title} href={getAbsoluteUrl(item.href, pageConfig)}>
                <Card.Body>
                  <Card.Copy>
                    <Card.Headline tag="h3">{item.title}</Card.Headline>
                    <Card.Description variant="pLarge">{item.description}</Card.Description>
                  </Card.Copy>
                  <Card.Action alignment="right">
                    <ChevronRightIcon />
                  </Card.Action>
                </Card.Body>
              </CardBase>
            ))}
          </ColumnTrack>
        </Section.Contents>
      </StyledSection>
    </Background>
  )
}

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    row-gap: 40px;

    ${({ theme }) => theme.breakpoints.up(`md`)} {
      row-gap: 56px;
    }
  }
`

const Background = styled.div`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
`

export default RecommendedReading
